import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Users, UserPlus, IndianRupee, TrendingUp, Calendar, Clock } from 'lucide-react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';

const OfflineDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const user = authService.getCurrentUser();
  const isManager = ['admin', 'manager', 'offline_manager'].includes(user?.role?.toLowerCase());

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = isManager 
        ? await api.getOfflineManagerDashboard({})
        : await api.getOfflineCounsellorDashboard({});
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  // Counsellor Dashboard
  if (!isManager) {
    return (
      <div className="space-y-6" data-testid="counsellor-dashboard">
        <h1 className="text-2xl font-bold text-slate-800">My Dashboard</h1>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg"><Users className="text-blue-600" size={24} /></div>
              <div>
                <p className="text-sm text-slate-500">Total Enquiries</p>
                <p className="text-2xl font-bold text-slate-800">{dashboardData?.enquiries?.total || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg"><UserPlus className="text-green-600" size={24} /></div>
              <div>
                <p className="text-sm text-slate-500">Converted</p>
                <p className="text-2xl font-bold text-green-600">{dashboardData?.enquiries?.converted || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg"><TrendingUp className="text-purple-600" size={24} /></div>
              <div>
                <p className="text-sm text-slate-500">Conversion Rate</p>
                <p className="text-2xl font-bold text-purple-600">{dashboardData?.enquiries?.conversion_rate || 0}%</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-teal-100 rounded-lg"><IndianRupee className="text-teal-600" size={24} /></div>
              <div>
                <p className="text-sm text-slate-500">Fee Collected</p>
                <p className="text-2xl font-bold text-teal-600">₹{(dashboardData?.fee_collection?.collected || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Fee Collection</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Total Fee</p>
              <p className="text-xl font-bold text-slate-800">₹{(dashboardData?.fee_collection?.total_fee || 0).toLocaleString()}</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600">Collected</p>
              <p className="text-xl font-bold text-green-700">₹{(dashboardData?.fee_collection?.collected || 0).toLocaleString()}</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-red-600">Pending</p>
              <p className="text-xl font-bold text-red-700">₹{(dashboardData?.fee_collection?.pending || 0).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Manager Dashboard
  return (
    <div className="space-y-6" data-testid="manager-dashboard">
      <h1 className="text-2xl font-bold text-slate-800">Offline Centre Dashboard</h1>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg"><Users className="text-blue-600" size={24} /></div>
            <div>
              <p className="text-sm text-slate-500">Total Enquiries</p>
              <p className="text-2xl font-bold text-slate-800">{dashboardData?.summary?.total_enquiries || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg"><UserPlus className="text-green-600" size={24} /></div>
            <div>
              <p className="text-sm text-slate-500">Total Admissions</p>
              <p className="text-2xl font-bold text-green-600">{dashboardData?.summary?.total_admissions || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-teal-100 rounded-lg"><IndianRupee className="text-teal-600" size={24} /></div>
            <div>
              <p className="text-sm text-slate-500">Fee Collected</p>
              <p className="text-2xl font-bold text-teal-600">₹{(dashboardData?.fee_collection?.collected || 0).toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-lg"><IndianRupee className="text-red-600" size={24} /></div>
            <div>
              <p className="text-sm text-slate-500">Fee Pending</p>
              <p className="text-2xl font-bold text-red-600">₹{(dashboardData?.fee_collection?.pending || 0).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Counsellor Performance */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-4 border-b bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-800">Counsellor Performance</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Counsellor</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Enquiries</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Converted</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Rate</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Collected</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Pending</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {dashboardData?.counsellor_performance?.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{c.name}</td>
                  <td className="px-4 py-3 text-slate-600">{c.enquiries}</td>
                  <td className="px-4 py-3 text-green-600">{c.converted}</td>
                  <td className="px-4 py-3 text-purple-600">{c.conversion_rate}%</td>
                  <td className="px-4 py-3 text-green-600">₹{c.collected?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-red-600">₹{c.pending?.toLocaleString()}</td>
                </tr>
              ))}
              {(!dashboardData?.counsellor_performance || dashboardData.counsellor_performance.length === 0) && (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">No counsellors found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Today's Classes */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-4 border-b bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-800">Today's Classes</h2>
        </div>
        {dashboardData?.todays_classes?.length > 0 ? (
          <div className="divide-y">
            {dashboardData.todays_classes.map((cls) => (
              <div key={cls.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-800">{cls.title}</p>
                  <p className="text-sm text-slate-500">{cls.subject} - {cls.teacher_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-600">{cls.batch_name}</p>
                  <p className="text-sm text-slate-500">{new Date(cls.scheduled_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-slate-500">No classes scheduled for today</div>
        )}
      </div>
    </div>
  );
};

export default OfflineDashboard;
